import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaCreditCard, FaCheckCircle, FaExclamationTriangle, FaLock } from 'react-icons/fa';
import { paymentProcessingService } from '../services/paymentProcessingService';

const AuthorizeNetTest = () => {
  const [testStatus, setTestStatus] = useState('ready');
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runConnectionTest = async () => {
    setLoading(true);
    setTestStatus('testing');
    
    try {
      // Check if Authorize.Net is configured
      const isConfigured = !!(
        import.meta.env.VITE_AUTHORIZE_NET_API_LOGIN_ID &&
        import.meta.env.VITE_AUTHORIZE_NET_TRANSACTION_KEY
      );

      if (isConfigured) {
        const isSandbox = import.meta.env.VITE_AUTHORIZE_NET_SANDBOX_MODE === 'true';
        
        setTestResult({
          success: true,
          message: 'Authorize.Net is configured and ready!',
          details: {
            apiLoginId: import.meta.env.VITE_AUTHORIZE_NET_API_LOGIN_ID?.substring(0, 4) + '****',
            mode: isSandbox ? 'SANDBOX' : 'PRODUCTION',
            endpoint: isSandbox ? 'apitest.authorize.net' : 'api.authorize.net',
            status: 'Active'
          }
        });
        setTestStatus('success');
      } else {
        setTestResult({
          success: false,
          message: 'Authorize.Net not configured',
          details: {
            error: 'Missing API credentials in .env file'
          }
        });
        setTestStatus('error');
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Configuration check failed',
        details: {
          error: error.message
        }
      });
      setTestStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const runTestTransaction = async () => {
    setLoading(true);
    setTestStatus('processing');
    
    try {
      // Create a test payment
      const testPayment = {
        amount: 1.00,
        type: 'test',  // Changed from paymentType to type
        paymentType: 'test',
        paymentMethod: {
          type: 'card',
          number: '4111111111111111',
          expiry: '12/25',
          cvv: '123'
        },
        customer: {
          firstName: 'Test',
          lastName: 'Customer',
          email: 'test@paradise-performance.com',
          phone: '555-0123',
          address: '123 Test St',
          city: 'Paradise',
          state: 'TX',
          zip: '76073'
        },
        metadata: {
          test: true,
          source: 'AuthorizeNetTest'
        }
      };

      const result = await paymentProcessingService.processPayment(testPayment);
      
      if (result.success) {
        setTestResult({
          success: true,
          message: 'Test transaction successful!',
          details: {
            transactionId: result.transactionId,
            authCode: result.authCode,
            amount: '$1.00',
            status: 'Approved'
          }
        });
        setTestStatus('success');
      } else {
        setTestResult({
          success: false,
          message: 'Transaction failed',
          details: {
            error: result.error || 'Transaction was declined'
          }
        });
        setTestStatus('error');
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Test transaction failed',
        details: {
          error: error.message,
          note: 'This is normal in development mode. Try testing from a vehicle detail page instead.'
        }
      });
      setTestStatus('warning');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto p-6 bg-adm-bg-1 rounded-adm-card shadow-adm-card"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-[22px] font-semibold tracking-[-0.01em] text-adm-text-hi">Authorize.Net Payment Test</h2>
        <FaLock className="text-green-600 w-6 h-6" />
      </div>

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <FaCreditCard className="text-blue-600 w-5 h-5 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900">Configuration Status</p>
            <p className="text-sm text-blue-700 mt-1">
              {import.meta.env.VITE_AUTHORIZE_NET_API_LOGIN_ID ? (
                <>
                  <span className="text-green-600 font-semibold">✓ Connected</span>
                  <span className="ml-2">API Login: {import.meta.env.VITE_AUTHORIZE_NET_API_LOGIN_ID?.substring(0, 4)}****</span>
                </>
              ) : (
                <span className="text-red-600">✗ Not configured - Add credentials to .env file</span>
              )}
            </p>
            <p className="text-sm text-blue-700 mt-1">
              Mode: <span className="font-semibold">
                {import.meta.env.VITE_AUTHORIZE_NET_SANDBOX_MODE === 'true' ? 'SANDBOX' : 'PRODUCTION'}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <button
          onClick={runConnectionTest}
          disabled={loading}
          className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          {loading && testStatus === 'testing' ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            <FaCheckCircle className="w-5 h-5" />
          )}
          <span>Test Connection</span>
        </button>

        <button
          onClick={runTestTransaction}
          disabled={loading || !import.meta.env.VITE_AUTHORIZE_NET_API_LOGIN_ID}
          className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          {loading && testStatus === 'processing' ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            <FaCreditCard className="w-5 h-5" />
          )}
          <span>Run Test Transaction ($1.00)</span>
        </button>
      </div>

      {testResult && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-6 p-4 rounded-lg ${
            testResult.success ? 'bg-green-50 border border-green-200' : 
            testStatus === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
            'bg-red-50 border border-red-200'
          }`}
        >
          <div className="flex items-start space-x-3">
            {testResult.success ? (
              <FaCheckCircle className="text-green-600 w-5 h-5 mt-0.5" />
            ) : testStatus === 'warning' ? (
              <FaExclamationTriangle className="text-yellow-600 w-5 h-5 mt-0.5" />
            ) : (
              <FaExclamationTriangle className="text-red-600 w-5 h-5 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`font-semibold ${
                testResult.success ? 'text-green-900' : 
                testStatus === 'warning' ? 'text-yellow-900' :
                'text-red-900'
              }`}>
                {testResult.message}
              </p>
              {testResult.details && (
                <div className="mt-2 text-sm space-y-1">
                  {Object.entries(testResult.details).map(([key, value]) => (
                    <p key={key} className={
                      testResult.success ? 'text-green-700' : 
                      testStatus === 'warning' ? 'text-yellow-700' :
                      'text-red-700'
                    }>
                      <span className="font-medium capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span> {value}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {import.meta.env.VITE_AUTHORIZE_NET_SANDBOX_MODE !== 'true' && (
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <FaExclamationTriangle className="text-yellow-600 w-5 h-5 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-semibold">⚠️ PRODUCTION MODE ACTIVE</p>
              <p className="mt-1">Real transactions will be processed. Test with small amounts only.</p>
              <p className="mt-1">To use test mode, set VITE_AUTHORIZE_NET_SANDBOX_MODE=true in .env</p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Quick Test Instructions:</h3>
        <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
          <li>Click "Test Connection" to verify API credentials</li>
          <li>Go to any vehicle listing page</li>
          <li>Click "Make Deposit" or "Purchase"</li>
          <li>Use test card: 4111 1111 1111 1111 (sandbox only)</li>
          <li>Check transactions at account.authorize.net</li>
        </ol>
      </div>
    </motion.div>
  );
};

export default AuthorizeNetTest;
